import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Car, Truck } from "lucide-react";
import { images } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, Overline, ease } from "@/components/Reveal";

function MobilityBlock({ image, icon: Icon, title, text, cta, to, testId, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease }}
      className="group relative overflow-hidden rounded-2xl border border-edge bg-asphalt"
    >
      <div className="relative h-60 overflow-hidden md:h-72">
        <img src={image} alt={title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/20 to-transparent" />
        <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-md border border-ice/20 bg-carbon/60 text-ice backdrop-blur-sm">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </span>
      </div>
      <div className="p-6 md:p-7">
        <h3 className="font-display text-xl font-bold text-ice">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-steel">{text}</p>
        <Link
          to={to}
          data-testid={testId}
          className="mt-5 inline-flex items-center gap-2 rounded-md border border-ice/25 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-ice transition-colors hover:border-ice hover:bg-ice hover:text-carbon"
        >
          {cta} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </motion.div>
  );
}

// Autonomous, compact "rental & sales" section — visually distinct from the emergency area.
export function MobilitySection() {
  const { t } = useLang();
  const m = t.mobility;
  return (
    <section id="mobilita" data-testid="mobility-section" className="relative overflow-hidden border-y border-edge bg-gradient-to-b from-asphalt to-carbon py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mb-10 max-w-2xl">
          <Overline>{m.overline}</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ice sm:text-4xl lg:text-5xl">{m.title}</h2>
          <p className="mt-3 font-display text-lg font-semibold text-amber">{m.subtitle}</p>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-steel">{m.text}</p>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <MobilityBlock image={images.rentalCar} icon={Car} title={m.rentTitle} text={m.rentText} cta={m.rentCta} to="/noleggio-auto" testId="mobility-rent-cta" delay={0} />
          <MobilityBlock image={images.saleCar} icon={Truck} title={m.saleTitle} text={m.saleText} cta={m.saleCta} to="/auto-in-vendita" testId="mobility-sale-cta" delay={0.1} />
        </div>
      </div>
    </section>
  );
}
